import {
    IAdBudget,
    IAdBudgetDisplay,
    BudgetPeriod,
    BudgetStatus,
    IAdMetrics,
    IAdMetricsDisplay,
    IPracticeByProviderRow,
    IPracticePoolBudget,
    IPracticeSaveRequest,
    IPracticePoolSaveSpecialtyBid,
    IPracticeSaveMember,
    IPracticeSaveMemberSpecialtyBid,
    SpecialtyOverrides,
    ListingStatus,
} from 'types/marketing';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * 分转换为元
 * @param cents 分
 * @returns 元
 */
export const centsToYuan = (cents?: number): number => {
    if (!cents) {
        return 0;
    }
    return cents / 100;
};

/**
 * 元转换为分
 * @param yuan 元
 * @returns 分
 */
export const yuanToCents = (yuan?: number): number => {
    if (!yuan) {
        return 0;
    }
    return Math.round(yuan * 100);
};

/**
 * 将IAdBudget转换为显示用的IAdBudgetDisplay
 * @param budget 原始预算数据
 * @returns 显示用预算数据
 */
export const convertBudgetToDisplay = (budget: IAdBudget): IAdBudgetDisplay => {
    return {
        id: budget.id,
        providerId: budget.providerId,
        budgetUuid: budget.budgetUuid,
        budgetPeriod: budget.budgetPeriod || BudgetPeriod.WEEKLY,
        budgetCap: centsToYuan(budget.budgetCapCents) || 300,
        currentPeriodStart: budget.currentPeriodStart,
        currentPeriodEnd: budget.currentPeriodEnd,
        spentThisPeriod: centsToYuan(budget.spentThisPeriodCents),
        remainingBudget: centsToYuan(budget.remainingBudgetCents),
        status: budget.status,
        autoRenew: budget.autoRenew,
        createdAt: budget.createdAt,
        updatedAt: budget.updatedAt,
        createdBy: budget.createdBy,
        updatedBy: budget.updatedBy,
        updatedByName: budget.updatedByName,
        generalBidAmount: centsToYuan(budget.generalBidAmountCents) || 0,
        enableAdvancedSettings: budget.enableAdvancedSettings || false,
        specialtyBids:
            budget.specialtyBids?.map((item) => ({
                specialtyName: item.specialtyName,
                bidAmount: centsToYuan(item.bidAmountCents),
                specialtyCap: item.specialtyCapCents ? centsToYuan(item.specialtyCapCents) : undefined,
                enabled: item.enabled,
            })) || [],
        creditBalance: {
            total: centsToYuan(budget.creditBalance?.totalCents) || 300,
            used: centsToYuan(budget.creditBalance?.usedCents) || 0,
            remaining: centsToYuan(budget.creditBalance?.remainingCents) || 0,
        },
    };
};

export const convertBudgetToSave = (budget: IAdBudgetDisplay): IAdBudget => {
    return {
        budgetPeriod: budget.budgetPeriod,
        budgetCapCents: yuanToCents(budget.budgetCap),
        generalBidAmountCents: yuanToCents(budget.generalBidAmount),
        enableAdvancedSettings: budget.enableAdvancedSettings,
        specialtyBids: budget.enableAdvancedSettings
            ? budget.specialtyBids?.map((item) => ({
                  specialtyName: item.specialtyName,
                  bidAmountCents: yuanToCents(item.bidAmount),
                  specialtyCapCents: item.specialtyCap ? yuanToCents(item.specialtyCap) : undefined,
                  enabled: item.enabled,
              })) || []
            : undefined,
        status: budget.status,
    };
};

/**
 * 检查预算是否即将耗尽
 * @param budget 预算数据
 * @param threshold 阈值百分比 (0-100)
 * @returns 是否即将耗尽
 */
export const isBudgetNearlyExhausted = (budget: IAdBudget, threshold: number = 80): boolean => {
    if (!budget.spentThisPeriodCents) {
        return false;
    }
    const utilizationPercentage =
        budget.budgetCapCents > 0 ? (budget.spentThisPeriodCents / budget.budgetCapCents) * 100 : 0;
    return utilizationPercentage >= threshold;
};

// 单个 provider 的预算是否已耗尽（来自 practice/ad/byProvider/list 的 budgetStatus）
export const isProviderBudgetExhausted = (row: Pick<IPracticeByProviderRow, 'budgetStatus'>): boolean =>
    row.budgetStatus === BudgetStatus.BUDGET_EXHAUSTED;

// provider 是否已上架（listed），只有 listed 才能加入 group campaign
export const isProviderListed = (row: Pick<IPracticeByProviderRow, 'listingStatus'>): boolean =>
    row.listingStatus === ListingStatus.LISTED;

// provider 未上架时，campaign toggle 禁用的原因说明（用于 tooltip）
export const getListingStatusTooltip = (listingStatus?: ListingStatus | null): string => {
    switch (listingStatus) {
        case ListingStatus.PENDING_REVIEW:
            return "This provider's listing is still under review. Pro-Listing will be available once they're listed.";
        case ListingStatus.PAUSE_LISTING:
            return "This provider's listing is paused, so Pro-Listing can't be turned on. Resume their listing to enable it.";
        case ListingStatus.UNLISTED:
        case ListingStatus.DISQUALIFIED:
        case ListingStatus.UNIPROFILE_NEEDED:
        default:
            return "This provider isn't on a listing yet, so Pro-Listing isn't available. List them first to enable it.";
    }
};

// provider 上架状态的 tag 展示（label + 颜色 variant），用于 provider list
export type ListingStatusTagVariant = 'listed' | 'unlisted' | 'pause' | 'pending';

export const getListingStatusTag = (
    listingStatus?: ListingStatus | null,
): { label: string; variant: ListingStatusTagVariant } => {
    switch (listingStatus) {
        case ListingStatus.LISTED:
            return { label: 'Listed', variant: 'listed' };
        case ListingStatus.PENDING_REVIEW:
            return { label: 'Pending review', variant: 'pending' };
        case ListingStatus.PAUSE_LISTING:
            return { label: 'Pause', variant: 'pause' };
        case ListingStatus.UNLISTED:
        case ListingStatus.DISQUALIFIED:
        case ListingStatus.UNIPROFILE_NEEDED:
        default:
            return { label: 'Unlisted', variant: 'unlisted' };
    }
};

// 补全日期数据，并计算impressionShareRate
function transformTrendsDataToDisplay(data: IAdMetrics, week: number) {
    // 全部是 utc 时间
    const { trends = [], pauseTimeRanges = [], joinedAt } = data;

    // 根据 week 参数确定日期范围
    const today = dayjs().startOf('day');
    const joinedAtDate = dayjs.utc(joinedAt).local().startOf('day'); // joinedAt从UTC转换为本地时间

    let startDate: dayjs.Dayjs;
    const endDate = today;

    if (week === 0) {
        // week = 0: 从 joinedAt 到今天
        startDate = joinedAtDate;
    } else {
        const weeksAgo = today.subtract(week * 7, 'day');
        // 取最大日期（更晚的日期）
        startDate = weeksAgo.isAfter(joinedAtDate) ? weeksAgo : joinedAtDate;
    }

    // 生成连续日期和数据
    const dates: string[] = [];
    const bookings: number[] = [];
    const impressionShareRate: number[] = [];

    let currentDate = startDate;
    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
        // 获取YYYY-MM-DD格式的日期字符串，与trends数据格式保持一致
        const dateKey = currentDate.format('YYYY-MM-DD');
        const trend = trends.find((t) => dayjs.utc(t.date).local().format('YYYY-MM-DD') === dateKey);

        // 格式化为MM/DD/YYYY格式用于显示
        dates.push(currentDate.format('MM/DD/YYYY'));
        bookings.push(trend?.bookings ?? 0);

        if (trend && trend.auctionParticipants && trend.impressions && trend.auctionParticipants > 0) {
            impressionShareRate.push(Math.round((trend.impressions / trend.auctionParticipants) * 100));
        } else {
            impressionShareRate.push(0);
        }

        currentDate = currentDate.add(1, 'day');
    }

    // pauseTimeRanges 去重并格式化
    const uniquePauseTimeRanges = pauseTimeRanges.filter((range, index, array) => {
        return array.findIndex((r) => r.start === range.start && r.end === range.end) === index;
    });
    const pauseTimeRangesDisplay = uniquePauseTimeRanges.map((r) => ({
        start: dayjs.utc(r.start).local().format('MM/DD/YYYY'),
        end: dayjs.utc(r.end).local().format('MM/DD/YYYY'),
    }));

    return {
        dates,
        bookings,
        impressionShareRate,
        pauseTimeRanges: pauseTimeRangesDisplay,
    };
}

export const convertAdMetricsToDisplay = (metrics: IAdMetrics, week: number): IAdMetricsDisplay => {
    const previousImpressionShareRate =
        metrics.previousAuctionParticipants && metrics.previousTotalImpressions
            ? Math.round((metrics.previousTotalImpressions / metrics.previousAuctionParticipants) * 100)
            : undefined;
    const impressionShareRate =
        metrics.auctionParticipants && metrics.totalImpressions
            ? Math.round((metrics.totalImpressions / metrics.auctionParticipants) * 100)
            : 0;
    return {
        totalBookings: metrics.totalBookings || 0,
        adBookings: metrics.adBookings,
        totalImpressions: metrics.totalImpressions || 0,
        totalSpend: centsToYuan(metrics.totalSpendCents),
        auctionParticipants: metrics.auctionParticipants,
        previousTotalBookings: metrics.previousTotalBookings,
        previousAdBookings: metrics.previousAdBookings,
        previousTotalImpressions: metrics.previousTotalImpressions,
        previousImpressionShareRate,
        impressionShareRate,
        bookingsChangeRate:
            metrics.adBookings && metrics.previousAdBookings
                ? Math.round(((metrics.adBookings - metrics.previousAdBookings) / metrics.previousAdBookings) * 100)
                : undefined,
        totalBookingsChangeRate:
            metrics.totalBookings && metrics.previousTotalBookings
                ? Math.round(
                      ((metrics.totalBookings - metrics.previousTotalBookings) / metrics.previousTotalBookings) * 100,
                  )
                : undefined,
        impressionsChangeRate:
            metrics.totalImpressions && metrics.previousTotalImpressions && metrics.auctionParticipants
                ? Math.round(
                      ((metrics.totalImpressions - metrics.previousTotalImpressions) /
                          metrics.previousTotalImpressions) *
                          100,
                  )
                : undefined,
        impressionShareChangeRate:
            previousImpressionShareRate && impressionShareRate
                ? Math.round(((impressionShareRate - previousImpressionShareRate) / previousImpressionShareRate) * 100)
                : undefined,
        trends: metrics.trends,
        trendsDisplay: metrics.trends ? transformTrendsDataToDisplay(metrics, week) : undefined,
        joinedAt: metrics.joinedAt,
    };
};

export const isInHours = (time?: string, hours = 24): boolean => {
    if (!time) {
        return false;
    }

    try {
        const createdDate = dayjs.utc(time); // 无时区信息，按 UTC 解析
        const now = dayjs.utc();

        const timeDifference = now.diff(createdDate, 'hour');

        // 如果时间差为负数（未来时间），返回 false
        if (timeDifference < 0) {
            return false;
        }
        return timeDifference <= hours;
    } catch (error) {
        console.error('Error parsing date:', time, error);
        return false;
    }
};

// 将 utc 时间转换成 MM/DD/YYYY
export const formatUTCTime = (utcTime?: string, format?: string): string => {
    if (!utcTime) {
        return '';
    }
    return dayjs
        .utc(utcTime)
        .local()
        .format(format || 'MM/DD/YYYY');
};

// --- Group (practice) pool budget helpers ---
// Shared by the provider group-campaign UI and the Ops group Pro-Listings modal.

const PracticeDefaultSpecialtyBid = 60;

export interface PracticePoolDisplay {
    strategyData: IAdBudgetDisplay;
    initialOverrides: SpecialtyOverrides;
    excludedProviders: Set<number>;
}

// Convert a pool budget response into the editable view models (strategy table + overrides + excluded set).
export function poolBudgetToDisplay(pool: IPracticePoolBudget): PracticePoolDisplay {
    // Fall back to the default bid when the backend has no configured default (0/null)
    const generalBidAmount = pool.defaultBidAmountCents
        ? centsToYuan(pool.defaultBidAmountCents)
        : PracticeDefaultSpecialtyBid;

    const strategyData: IAdBudgetDisplay = {
        budgetPeriod: (pool.pool?.budgetPeriod as BudgetPeriod) || BudgetPeriod.WEEKLY,
        budgetCap: pool.pool ? centsToYuan(pool.pool.budgetCapCents) : 300,
        generalBidAmount,
        status: (pool.pool?.status as BudgetStatus) || undefined,
        enableAdvancedSettings: pool.pool?.advancedSettingsSwitch || false,
        specialtyBids: pool.specialtyBids?.map((sb) => ({
            specialtyName: sb.specialtyName,
            bidAmount: centsToYuan(sb.bidAmountCents),
            specialtyCap: sb.specialtyCapCents ? centsToYuan(sb.specialtyCapCents) : undefined,
            enabled: sb.enabled,
        })),
    };

    const specBids: Record<string, number> = {};
    const specCaps: Record<string, string> = {};
    const disabledSpecs = new Set<string>();
    pool.specialtyBids?.forEach((sb) => {
        const key = sb.specialtyName.toLowerCase().replace(/\s+/g, '_');
        specBids[sb.specialtyName] = centsToYuan(sb.bidAmountCents);
        if (sb.specialtyCapCents) {
            specCaps[sb.specialtyName] = String(centsToYuan(sb.specialtyCapCents));
        }
        if (!sb.enabled) disabledSpecs.add(key);
    });

    const provBids: Record<string, Record<number, number>> = {};
    const provCaps: Record<string, Record<number, string>> = {};
    const disabledProvSpecs: Record<string, Set<number>> = {};
    const excludedProviders = new Set<number>();

    pool.members?.forEach((m) => {
        if (!m.enrolled) {
            excludedProviders.add(m.providerId);
            return;
        }
        m.specialtyBids?.forEach((sb) => {
            const key = sb.specialtyName.toLowerCase().replace(/\s+/g, '_');
            if (sb.bidAmountCents != null) {
                if (!provBids[key]) provBids[key] = {};
                provBids[key][m.providerId] = centsToYuan(sb.bidAmountCents);
            }
            if (sb.specialtyCapCents) {
                if (!provCaps[key]) provCaps[key] = {};
                provCaps[key][m.providerId] = String(centsToYuan(sb.specialtyCapCents));
            }
            if (sb.enabled === false) {
                if (!disabledProvSpecs[key]) disabledProvSpecs[key] = new Set();
                disabledProvSpecs[key].add(m.providerId);
            }
        });
    });

    return {
        strategyData,
        initialOverrides: { specBids, specCaps, provBids, provCaps, disabledSpecs, disabledProvSpecs },
        excludedProviders,
    };
}

// Build the pool budget saveOrUpdate request body from the current editing state.
export function buildPracticeSaveRequest(
    strategyData: IAdBudgetDisplay,
    providers: IPracticeByProviderRow[],
    budgetExcluded: Set<number>,
    overrides?: SpecialtyOverrides,
): IPracticeSaveRequest {
    const activeProviders = providers.filter((p) => !budgetExcluded.has(p.providerId));
    const defaultBidCents = yuanToCents(strategyData.generalBidAmount);

    const specialtyBids: IPracticePoolSaveSpecialtyBid[] = [];
    if (strategyData.enableAdvancedSettings && overrides) {
        const specNames = new Set<string>();
        activeProviders.forEach((p) => {
            p.services?.forEach((svc) => {
                if (svc.specialtyName) specNames.add(svc.specialtyName);
            });
        });

        specNames.forEach((name) => {
            const key = name.toLowerCase().replace(/\s+/g, '_');
            const disabled = overrides.disabledSpecs.has(key);
            const bidAmount = overrides.specBids[name] ?? strategyData.generalBidAmount;
            const capStr = overrides.specCaps[name];
            specialtyBids.push({
                specialtyName: name,
                bidAmountCents: yuanToCents(bidAmount),
                specialtyCapCents: capStr ? yuanToCents(parseFloat(capStr)) : null,
                enabled: !disabled,
            });
        });
    }

    const enrolledCount = activeProviders.length || 1;
    const perProviderCents = Math.floor(yuanToCents(strategyData.budgetCap || 0) / enrolledCount);

    const members: IPracticeSaveMember[] = providers.map((p) => {
        const enrolled = !budgetExcluded.has(p.providerId);
        const bidAmountCents = enrolled ? defaultBidCents : null;
        const capCents: number | null = perProviderCents;

        const memberSpecBids: IPracticeSaveMemberSpecialtyBid[] | undefined = overrides
            ? p.services
                  ?.filter((svc) => svc.specialtyName)
                  .map((svc) => {
                      const specKey = svc.specialtyName.toLowerCase().replace(/\s+/g, '_');
                      const provDisabled = overrides.disabledProvSpecs[specKey]?.has(p.providerId);
                      const provBid = overrides.provBids[specKey]?.[p.providerId];
                      const specBid = overrides.specBids[svc.specialtyName];
                      const resolvedBid = provBid ?? specBid;
                      return {
                          specialtyName: svc.specialtyName,
                          bidAmountCents: resolvedBid !== undefined ? yuanToCents(resolvedBid) : null,
                          specialtyCapCents: overrides.provCaps[specKey]?.[p.providerId]
                              ? yuanToCents(parseFloat(overrides.provCaps[specKey][p.providerId]))
                              : null,
                          enabled: !provDisabled,
                      };
                  })
            : undefined;

        return { providerId: p.providerId, enrolled, bidAmountCents, capCents, specialtyBids: memberSpecBids };
    });

    return {
        poolCapCents: yuanToCents(strategyData.budgetCap || 0),
        budgetPeriod: strategyData.budgetPeriod,
        defaultBidAmountCents: defaultBidCents,
        advancedSettingsSwitch: strategyData.enableAdvancedSettings || false,
        specialtyBids,
        members,
    };
}
